import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Backdrop, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import ScheduleHeaderImageInput, { parseHeaderImageFormData } from './Inputs/ScheduleHeaderImageInput';
import ScheduleIntroSectionForm, { parseIntroSectionFormData } from './Inputs/ScheduleIntroSectionForm';
import ScheduleEmailContentForm, { parseScheduleEmailContentFormData } from './ScheduleEmailContentForm';

interface CustomScheduleFormData {
  [key: string]: string;
  webImageId: string;
  mobileImageId: string;
  headerUrl: string;
  introHeading: string;
  introCopy: string;
  introCta: string;
  introCtaUrl: string;
  emailPreHeader: string;
  emailSubjectLine: string;
}

export interface CustomScheduleFormRef {
  getFormData: () => Promise<CustomScheduleFormData | null>;
  validateForms: () => boolean;
}

interface Props {
  countryGroupId: string;
  countryId: string;
  offerIds: Array<string>;
  schedule?: Partial<CustomerCommunication.CustomSchedule>;
  busy?: boolean;
}

const CustomScheduleContentForm = forwardRef<CustomScheduleFormRef, Props>((props, ref) => {
  const { countryGroupId, countryId, offerIds, schedule, busy = false } = props;

  const emailContentFormRef = useRef<HTMLFormElement>(null);
  const heroImageFormRef = useRef<HTMLFormElement>(null);
  const introSectionFormRef = useRef<HTMLFormElement>(null);

  const [webImageId, setWebImageId] = useState(schedule?.introSection?.headerDesktopImage || '');
  const [mobileImageId, setMobileImageId] = useState(schedule?.introSection?.headerMobileImage || '');
  const [headerUrl, setHeaderUrl] = useState(schedule?.introSection?.headerUrl || '');
  const [introHeading, setIntroHeading] = useState(schedule?.introSection?.introHeading || '');
  const [introCopy, setIntroCopy] = useState(schedule?.introSection?.introCopy || '');
  const [introCta, setIntroCta] = useState(schedule?.introSection?.introCta || '');
  const [introCtaUrl, setIntroCtaUrl] = useState(schedule?.introSection?.introCtaUrl || '');
  const [addHeader, setAddHeader] = useState(
    !!(
      schedule?.introSection?.headerDesktopImage ||
      schedule?.introSection?.headerMobileImage ||
      schedule?.introSection?.headerUrl
    ),
  );
  const [addIntroSection, setAddIntroSection] = useState(
    !!(
      schedule?.introSection?.introHeading ||
      schedule?.introSection?.introCopy ||
      schedule?.introSection?.introCta ||
      schedule?.introSection?.introCtaUrl
    ),
  );

  useEffect(() => {
    if (schedule?.introSection) {
      setWebImageId(schedule.introSection.headerDesktopImage ?? '');
      setMobileImageId(schedule.introSection.headerMobileImage ?? '');
      setHeaderUrl(schedule.introSection.headerUrl ?? '');
      setIntroHeading(schedule.introSection.introHeading ?? '');
      setIntroCopy(schedule.introSection.introCopy ?? '');
      setIntroCta(schedule.introSection.introCta ?? '');
      setIntroCtaUrl(schedule.introSection.introCtaUrl ?? '');
      setAddHeader(
        !!(
          schedule.introSection.headerDesktopImage ||
          schedule.introSection.headerMobileImage ||
          schedule.introSection.headerUrl
        ),
      );
      setAddIntroSection(
        !!(
          schedule.introSection.introHeading ||
          schedule.introSection.introCopy ||
          schedule.introSection.introCta ||
          schedule.introSection.introCtaUrl
        ),
      );
    }
  }, [schedule]);

  const handleToggleHeader = () => {
    if (addHeader) {
      setWebImageId('');
      setMobileImageId('');
      setHeaderUrl('');
    }
    setAddHeader(!addHeader);
  };

  const handleToggleIntroSection = () => {
    if (addIntroSection) {
      setIntroHeading('');
      setIntroCopy('');
      setIntroCta('');
      setIntroCtaUrl('');
    }
    setAddIntroSection(!addIntroSection);
  };

  const validateForms = useCallback(() => {
    return (
      emailContentFormRef.current?.reportValidity() &&
      (!addHeader || heroImageFormRef.current?.reportValidity()) &&
      (!addIntroSection || introSectionFormRef.current?.reportValidity())
    );
  }, [addHeader, addIntroSection]);

  const getFormData = useCallback(async () => {
    if (!validateForms()) {
      return null;
    }

    const emailContentData = parseScheduleEmailContentFormData(new FormData(emailContentFormRef.current));
    const headerImageData = addHeader
      ? parseHeaderImageFormData(new FormData(heroImageFormRef.current))
      : { webImageId: '', mobileImageId: '', headerUrl: '' };
    const introSectionData = addIntroSection
      ? parseIntroSectionFormData(new FormData(introSectionFormRef.current))
      : { introHeading: '', introCopy: '', introCta: '', introCtaUrl: '' };

    const formDataValues: CustomScheduleFormData = {
      ...emailContentData,
      webImageId: headerImageData.webImageId,
      mobileImageId: headerImageData.mobileImageId,
      headerUrl: headerImageData.headerUrl,
      introHeading: introSectionData.introHeading,
      introCopy: introSectionData.introCopy,
      introCta: introSectionData.introCta,
      introCtaUrl: introSectionData.introCtaUrl,
    };
    return formDataValues;
  }, [validateForms, addHeader, addIntroSection]);

  useImperativeHandle(ref, () => ({
    getFormData,
    validateForms,
  }));

  return (
    <Stack direction="column" gap={3}>
      <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
        <Stack direction="column" gap={2}>
          <Typography variant="h4">Email Content</Typography>
          <ScheduleEmailContentForm
            ref={emailContentFormRef}
            preHeaderDefaultValue={schedule?.emailPreheader}
            subjectLineDefaultValue={schedule?.emailSubjectLine}
            countryGroupId={countryGroupId}
            countryId={countryId}
            offerIds={offerIds}
            showPrefillButton={false}
          />
        </Stack>
        <Backdrop open={busy} sx={{ position: 'absolute' }}>
          <CircularProgress color="primary" />
        </Backdrop>
      </Paper>

      <Stack direction="row" spacing={2}>
        <Button
          variant={addHeader ? 'outlined' : 'contained'}
          color={addHeader ? 'error' : 'primary'}
          size="small"
          onClick={handleToggleHeader}
        >
          {addHeader ? 'Remove Header' : 'Add Header'}
        </Button>

        <Button
          variant={addIntroSection ? 'outlined' : 'contained'}
          color={addIntroSection ? 'error' : 'primary'}
          size="small"
          onClick={handleToggleIntroSection}
        >
          {addIntroSection ? 'Remove Intro Section' : 'Add Intro Section'}
        </Button>
      </Stack>

      {addHeader && (
        <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
          <Typography variant="h4">Hero Image</Typography>
          <Stack direction="column" gap={2}>
            <form ref={heroImageFormRef}>
              <ScheduleHeaderImageInput
                webImageId={webImageId}
                mobileImageId={mobileImageId}
                headerUrl={headerUrl}
                onWebImageIdChange={setWebImageId}
                onMobileImageIdChange={setMobileImageId}
                onHeaderUrlChange={setHeaderUrl}
                isOptional
              />
            </form>
          </Stack>
          <Backdrop open={busy} sx={{ position: 'absolute' }}>
            <CircularProgress color="primary" />
          </Backdrop>
        </Paper>
      )}

      {addIntroSection && (
        <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
          <Typography variant="h4">Intro Section</Typography>
          <Stack direction="column" gap={2}>
            <ScheduleIntroSectionForm
              ref={introSectionFormRef}
              introHeading={introHeading}
              introCopy={introCopy}
              introCta={introCta}
              introCtaUrl={introCtaUrl}
              onIntroHeadingChange={setIntroHeading}
              onIntroCopyChange={setIntroCopy}
              onIntroCtaChange={setIntroCta}
              onIntroCtaUrlChange={setIntroCtaUrl}
              isOptional
            />
          </Stack>
        </Paper>
      )}
    </Stack>
  );
});

CustomScheduleContentForm.displayName = 'CustomScheduleContentForm';

export default CustomScheduleContentForm;
